import { useEffect, useEffectEvent, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';

import {
    BudgetRequestsDocument,
    IBudgetRequestListItemFragment,
    IBudgetRequestStatus,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';

interface IUseBudgetRequestsOptions {
    offset: number;
    first: number;
    status?: IBudgetRequestStatus[];
    q?: string;
    userIds?: string[];
}

export const useBudgetRequests = ({
    offset,
    first,
    status,
    q,
    userIds,
}: IUseBudgetRequestsOptions) => {
    const { showApolloError } = useApolloError();

    const { data, loading, error } = useQuery(BudgetRequestsDocument, {
        variables: {
            userIds,
            offset,
            first,
            status,
            q: q || undefined,
        },
        fetchPolicy: 'cache-and-network',
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const count = data?.budgetRequests?.count;

    const budgetRequests = useMemo(
        () =>
            data?.budgetRequests?.edges
                .map((edge) => edge?.node)
                .filter(
                    (node): node is IBudgetRequestListItemFragment => !!node
                ) ?? [],
        [data]
    );

    return { budgetRequests, count, loading };
};
